import { useQuery } from '@tanstack/react-query';
import { FormField, Select } from '../../../components/ui/FormField.js';
import { api } from '../../../services/api.js';
import { RESOURCE_KEYS } from '../../../services/resourceKeys.js';
import type { Taxon } from '../CarFormPage.js';

/**
 * Brand→model cascade. State stays owned by the parent (`brandId`/`modelId` feed the parent's
 * submit payload and its hydration effect) — this component owns only the option queries and
 * the rendering, and relocates the "changing brand clears model" rule unchanged.
 */
export function BrandModelFields({
  brandId,
  modelId,
  onBrandChange,
  onModelChange,
}: {
  brandId: string;
  modelId: string;
  onBrandChange: (id: string) => void;
  onModelChange: (id: string) => void;
}) {
  const { data: brands } = useQuery({
    queryKey: RESOURCE_KEYS.carBrand.list,
    queryFn: () => api.get<Taxon[]>('/car-brands?status=active'),
  });
  // Models cascade on the selected brand.
  const { data: models } = useQuery({
    queryKey: [...RESOURCE_KEYS.carModel.list, brandId],
    queryFn: () => api.get<Taxon[]>(`/car-models?brand_id=${brandId}&status=active`),
    enabled: Boolean(brandId),
  });

  return (
    <>
      <FormField label="Brand" name="brand_id" required>
        <Select
          id="brand_id"
          value={brandId}
          onChange={(e) => {
            onModelChange('');
            onBrandChange((e.target as HTMLSelectElement).value);
          }}
        >
          <option value="">Select a brand…</option>
          {brands?.map((b) => (
            <option key={b._id} value={b._id}>
              {b.name}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Model" name="model_id">
        <Select
          id="model_id"
          value={modelId}
          disabled={!brandId}
          onChange={(e) => onModelChange((e.target as HTMLSelectElement).value)}
        >
          <option value="">{brandId ? 'Select a model…' : 'Pick a brand first'}</option>
          {models?.map((m) => (
            <option key={m._id} value={m._id}>
              {m.name}
            </option>
          ))}
        </Select>
      </FormField>
    </>
  );
}
